import './client-details.scss'

import { useMachine } from '@xstate/react'
import { useEffect, useMemo } from 'react'

import {
  GenericValidationMessages,
  Icon,
  IconButton,
  SaveSuccessMessage,
  Space
} from '@/components/core-v2'

import { ClientsState } from '../clients/clients.state'
import { ClientEditor } from './client-editor'
import { editClientMachine } from './state/edit-client-machine.state'
import { addMissingStrategies } from './utils/addMissingStrategies'

export function EditClient({
  clientState,
  onSave
}: {
  clientState: ClientsState
  onSave: () => void
}) {
  const [machine, send] = useMachine(editClientMachine)
  const state = machine.context

  const {
    availableFeeTypes,
    counterpartyIds: availableCptyIds,
    depositInstruments: availableDepositInstruments,
    isClientSavedSuccessfully,
    loanInstruments: availableLoanInstruments,
    selectedClient: client,
    strategies
  } = clientState

  const clientWithStrategies = useMemo(
    () => addMissingStrategies({ state: clientState }),
    [clientState]
  )

  useEffect(() => {
    send({ type: 'INIT', client: clientWithStrategies, onSave })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client])

  return (
    <>
      <ClientEditor
        existingClient
        availableCptyIds={availableCptyIds}
        availableDepositInstruments={availableDepositInstruments}
        availableFeeTypes={availableFeeTypes}
        availableLoanInstruments={availableLoanInstruments}
        client={state.client?.clientId ? state.client : client}
        clientName={client.clientName}
        errors={state.errors}
        newCPLeverageFee={state.newCPLeverageFee}
        newContProtFee1={state.newContProtFee1}
        newContProtFee2={state.newContProtFee2}
        newContProtFee3={state.newContProtFee3}
        newContProtFee4={state.newContProtFee4}
        newDelta1Fee={state.newDelta1Fee}
        newNoteFee={state.newNoteFee}
        newRiskManagedFee={state.newRiskManagedFee}
        newTermProtFee1={state.newTermProtFee1}
        newTermProtFee2={state.newTermProtFee2}
        newTermProtFee3={state.newTermProtFee3}
        newTermProtFee4={state.newTermProtFee4}
        newVCLeverageFee={state.newVCLeverageFee}
        strategies={strategies}
        onMutate={mutation => send({ type: 'MUTATE', mutation })}
        onMutateCPLevFee={mutation => send({ type: 'MUTATE_CPLEV_FEE', mutation })}
        onMutateContProtFee1={mutation => send({ type: 'MUTATE_CONT_PROT_FEE1', mutation })}
        onMutateContProtFee2={mutation => send({ type: 'MUTATE_CONT_PROT_FEE2', mutation })}
        onMutateContProtFee3={mutation => send({ type: 'MUTATE_CONT_PROT_FEE3', mutation })}
        onMutateContProtFee4={mutation => send({ type: 'MUTATE_CONT_PROT_FEE4', mutation })}
        onMutateDelta1Fee={mutation => send({ type: 'MUTATE_DELTA1_FEE', mutation })}
        onMutateNoteFee={mutation => send({ type: 'MUTATE_NOTE_FEE', mutation })}
        onMutateRiskManagedFee={mutation => send({ type: 'MUTATE_RISK_MANAGED_FEE', mutation })}
        onMutateTermProtFee1={mutation => send({ type: 'MUTATE_TERM_PROT_FEE1', mutation })}
        onMutateTermProtFee2={mutation => send({ type: 'MUTATE_TERM_PROT_FEE2', mutation })}
        onMutateTermProtFee3={mutation => send({ type: 'MUTATE_TERM_PROT_FEE3', mutation })}
        onMutateTermProtFee4={mutation => send({ type: 'MUTATE_TERM_PROT_FEE4', mutation })}
        onMutateVCLevFee={mutation => send({ type: 'MUTATE_VCLEV_FEE', mutation })}
      />
      <GenericValidationMessages errorMessages={state.errorMessages} />
      <Space isflex alignItems="center" mb={6} mx={5}>
        <IconButton
          disabled={!state.isSaveEnabled}
          icon={<Icon name="save" size="default" />}
          iconPosition="left"
          operationType="progress"
          text="Save client"
          onClick={() => send({ type: 'SAVE', client: state.client })}
        />
        <Space ml={5}>
          <SaveSuccessMessage isVisible={isClientSavedSuccessfully}>
            Client saved successfully!
          </SaveSuccessMessage>
        </Space>
      </Space>
    </>
  )
}
